import Alpine from 'alpinejs'
import { fragment } from '@js/util/helpers.js'

// Define (mobile) menu open/close state
Alpine.store('menu', false)
Alpine.store('desktopActiveMegaMenu', false)
Alpine.store('mobileActiveMegaMenu', false)

// Use @media query for hovering devices
let canHover = window.matchMedia('(hover: hover) and (pointer: fine)')
Alpine.store('canHover', () => canHover.matches)

// Define transition options for menu animations
Alpine.store('menuTransitions', {
  // The whole mobile menu when toggled
  mobileMenuContainer: {
    css: {
      // opacity: 0,
      transform: 'translateY(-100%) translateY(-35px)',
    },
    duration: 500,

    // Visuals controlled by the global 'menu' state
    bind: 'menu',
  },

  // A mega menu appearing/disappearing on mobile
  mobileMegaMenu: {
    css: {
      transform: 'translateX(100%) translateX(35px)',
    },
    duration: 400,
  },

  // A mega menu appearing/disappearing on desktop
  desktopMegaMenu: {
    css: {
      transform: 'translateY(-100%) translateY(calc(-1 * var(--header-height))) translateY(-35px)',
    },
    duration: 600,
  },

  // Search appearing on desktop
  desktopSearch: {
    css: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    duration: 200,

    // Avoid pointer interactivity to avoid accidental closing/re-opening during transition
    onBeforeAnimate(element) {
      element.style.pointerEvents = 'none'
    },
    onComplete(element) {
      element.style.removeProperty('pointer-events')
    },
  },
})

let svgIcon =
  document.querySelector('#mega-menu-icon-enter')?.content.firstElementChild ??
  fragment`<svg aria-hidden="true" focusable="false" width="7" height="11" viewBox="0 0 7 11" xmlns="http://www.w3.org/2000/svg"><path d="M1.556 11 0 9.429 3.89 5.5 0 1.571 1.556 0 7 5.5z" fill="currentColor" fill-rule="evenodd"/></svg>`

// We'll attach the Alpine menu logic via JS because it would be *very* tedious
// to do so via the various hooks WordPress provides for menu HTML generation.
document.querySelectorAll('nav.main').forEach(menuNavContainer => {
  let trigger =
    menuNavContainer.closest('[data-mega-menu-trigger]')?.dataset.megaMenuTrigger ?? 'click'

  let isMobile = menuNavContainer.matches('.header-mobile-wrapper *')

  menuNavContainer.querySelectorAll('.menu-item a').forEach(menuItemLink => {
    if (
      isMobile &&
      menuItemLink.getAttribute('href') !== '#' &&
      menuItemLink.getAttribute('href').startsWith('#')
    ) {
      menuItemLink.setAttribute('x-on:click', '$store.menu = false')
    }
  })

  // Iterate all list items that have submenus
  menuNavContainer.querySelectorAll('.mega-menu-trigger').forEach(menuItemLink => {
    let menuItem = menuItemLink.parentNode
    menuItem.classList.add('with-mega-menu')

    let megaMenu = menuItemLink
      .closest('.header-wrapper')
      .querySelector(`.mega-menu[data-mega-menu-id="${menuItemLink.dataset.megaMenuId}"]`)
    let megaMenuId = menuItemLink.dataset.megaMenuId
    let transitionId = `megaMenu_${megaMenuId}`

    // Make submenu focusable as a whole and hide it
    megaMenu.setAttribute('tabindex', '-1')

    if (isMobile) {
      /*******************************
       * Mobile menu specific functionality
       ******************************/

      // Hide mega menu unless it's the current menu item
      if (!menuItem.classList.contains('current-menu-parent')) {
        megaMenu.style.display = 'none'
      }

      // Add unique ID to mega menu for ARIA markup
      let megaMenuIdAttribute = `mega-menu-${megaMenuId}`

      megaMenu.setAttribute('id', megaMenuIdAttribute)

      // Disable link clickability if it points to empty anchor
      // Make it show the mega menu instead

      if (menuItemLink.getAttribute('href') === '#' || trigger === 'click') {
        menuItemLink.setAttribute('x-on:click.prevent', `${transitionId}.show()`)
      }

      // Insert dropdown icon after link
      menuItemLink.after(fragment`
      <button type="button" class="dropdown" @click="${transitionId}.toggle()" :aria-expanded="${transitionId}.isVisible()" :aria-controls="${JSON.stringify(
        megaMenuIdAttribute,
      )}">
        ${svgIcon.cloneNode(true)}
      </button>
    `)
    } else {
      /*******************************
       * Desktop menu specific functionality
       ******************************/

      menuItem.setAttribute(
        ':data-with-active-mega-menu',
        `$store.desktopActiveMegaMenu === ${JSON.stringify(megaMenuId)}`,
      )

      let megaMenuSelector = `.header-desktop-wrapper .mega-menu[data-mega-menu-id="${menuItemLink.dataset.megaMenuId}"]`

      megaMenu.style.display = 'none'

      // Toggle menu on mouseenter/mouseleave for mouse navigation
      megaMenu.setAttribute(':style', '{ zIndex: latestEl === $el ? zIndex : null }')
      menuItem.setAttribute('x-data', '{ megaMenu: null }')
      menuItem.setAttribute(
        'x-init',
        `megaMenu = document.querySelector(${JSON.stringify(megaMenuSelector)})`,
      )

      switch (trigger) {
        case 'hover': {
          // Insert focusable element before link
          menuItemLink.before(
            fragment`<div x-show="$data.${transitionId}?.isVisible()" class="sr-only" tabindex="0" @focus="$store.desktopActiveMegaMenu = false"></div>`,
          )

          // Insert dropdown button after link
          menuItemLink.after(
            fragment`
              <div x-show="$data.${transitionId}?.isVisible()" class="sr-only" tabindex="0" @focus="megaMenu.focus()"></div>
              <button type="button" class="dropdown" @click="${transitionId}.show()" tabindex="-1">
                <svg aria-hidden="true" focusable="false" width="7" height="4" viewBox="0 0 7 4" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 4c-.277 0-.542-.115-.738-.32L0 .775.738 0 3.5 2.903 6.262 0 7 .776 4.238 3.679A1.015 1.015 0 0 1 3.5 4Z" fill="currentColor" fill-rule="nonzero"/></svg>
              </button>
            `,
          )

          menuItem.setAttribute(
            'x-on:mouseenter',
            `
            zIndex++
            latestEl = megaMenu
            if ($store.canHover()) {
              ${transitionId}.show()
            }
            `,
          )

          // Toggle menu on focusin/focusout for keyboard navigation
          menuItem.setAttribute(
            'x-on:focusin',
            `
            (
              // Dropdown click
              $event.target.matches('button, button *') ||

              // Keyboard focus
              document.documentElement.classList.contains('no-focus-visible') ||
              $el.querySelector('a').matches(':focus-visible')
            ) &&

            ${transitionId}.show()
            `,
          )
          megaMenu.setAttribute(
            'x-on:focusout',
            `setTimeout(() => {
              // Timeout is needed because in the moment the event occurs, there is no focused element
              !${transitionId}.isAnimating($el) && !$el.querySelector(':focus') && ${transitionId}.hide()
            }, 0)`,
          )
          break
        }

        case 'click': {
          menuItemLink.setAttribute(
            'x-on:click.prevent',
            `
            ${transitionId}.toggle()
            let isVisible = ${transitionId}.isVisible()

            if (isVisible) {
              setTimeout(() => {
                megaMenu.focus()
              }, 0)
            }
            `,
          )
          break
        }

        default:
          throw new Error(`Unknown trigger type: ${trigger}`)
      }
    }

    menuItem.setAttribute(
      ':class',
      `$data.${transitionId}?.isVisible() ? 'rotate-dropdown-arrow' : null`,
    )
  })
})
